import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CalendarCheck } from 'lucide-react';

import { useHolidayCalendarApi } from './config/index';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';

// Tatil takvimleri (AKM POC §5.4). Küresel takvimler salt okunur HAZIR ŞABLONDUR — yalnız superAdmin yazar;
// kiracı (EM.Write) kendi şirket takvimini oluşturur ya da hazır şablonu KOPYALAR. superAdmin ayrıca Türkiye
// resmî tatil takvimini kurar ve uygun kiracılara atar (önce dryRun özeti, onaydan sonra gerçek çalıştırma).
const Container = styled.div``;

const Note = styled.div`
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0 0 12px; line-height: 1.4;
  background: ${({ $tone }) => ($tone === 'success' ? '#f0fdf4' : '#fff7ed')};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? '#bbf7d0' : '#fed7aa')};
  color: ${({ $tone }) => ($tone === 'success' ? '#166534' : '#9a3412')};
`;

const EMPTY_DELETE = { isOpen: false, ids: [], name: '' };

// Satır yazılabilir mi: küresel şablonu yalnız superAdmin, şirket takvimini EM.Write yazar (backend WritableAsync).
export const canEditCalendar = (row, { isSuperAdmin, canWriteTenant }) => {
  if (!row) return false;
  return row.isGlobal ? !!isSuperAdmin : !!canWriteTenant;
};

export default function HolidayCalendarsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { isSuperAdmin, canWrite } = useAuth();
  const canWriteTenant = canWrite('EM');
  const canCreate = !!isSuperAdmin || canWriteTenant;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(EMPTY_DELETE);
  const [deleting, setDeleting] = useState(false);
  const [seedPreview, setSeedPreview] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedDone, setSeedDone] = useState(false);

  const {
    objectSchema, fetching, loadFailed, columns: baseColumns, data, pagination, create, update, _delete, copy, seedTurkey,
  } = useHolidayCalendarApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  // ── Kopyala
  const handleCopy = useCallback((row) => setCopyTarget(row), []);

  const copySchema = useMemo(() => [
    { name: 'name', label: t('holidayCalendar.copyName'), type: 'text', grid: 12, required: true },
  ], [t]);

  // GenericFormDrawer açıkken initialValues kimliği değişirse formu sıfırlar — hedef değişmedikçe sabit.
  const copyInitialValues = useMemo(() => (copyTarget ? { name: copyTarget.name || '' } : null), [copyTarget]);

  const handleCopySave = useCallback(async (formData) => {
    if (!copyTarget) return false;
    const ok = await copy(copyTarget.id, (formData?.name || '').trim());
    if (ok) setCopyTarget(null);
    return ok;
  }, [copyTarget, copy]);

  // ── Silme
  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('holidayCalendar.title')}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm(EMPTY_DELETE);
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  // ── Türkiye takvimi kurulumu (superAdmin): önce dryRun özeti, onaydan sonra dryRun=false.
  const handleTurkeyClick = useCallback(async () => {
    setSeedDone(false);
    setSeeding(true);
    const preview = await seedTurkey(true);
    setSeeding(false);
    if (preview) setSeedPreview(preview);
  }, [seedTurkey]);

  const handleTurkeyConfirm = useCallback(async () => {
    setSeeding(true);
    const result = await seedTurkey(false);
    setSeeding(false);
    if (!result) return; // hata toast'u interceptor'da; özet penceresi açık kalır
    setSeedPreview(null);
    // Başarı bildirimi toast DEĞİL (toast yalnız interceptor'da): sayfadaki bilgi satırı.
    setSeedDone(true);
  }, [seedTurkey]);

  const seedSummary = seedPreview ? t('holidayCalendar.turkeySeedSummary', {
    added: seedPreview.addedHolidayCount ?? 0,
    assigned: (seedPreview.assigned || []).length,
    ambiguous: (seedPreview.skippedAmbiguousCountry || []).length,
  }) : '';

  const handleRowClick = useCallback((row) => {
    history.push(`/holiday-calendars/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Satır eylemleri kapsama göre: hazır şablonda kiracı kullanıcısı YALNIZ "Kopyala" görür (detay satır
  // tıklamasıyla salt okunur açılır); yazılabilir satırda detay/düzenle/sil. Ayraç yok — koşullu kalemler
  // arasında yetim ayraç kalırdı.
  const actionOptions = useMemo(() => {
    const auth = { isSuperAdmin, canWriteTenant };
    return [
      { type: 'details', label: t('holidayCalendar.detail'), onClick: handleRowClick, condition: (row) => canEditCalendar(row, auth) },
      { type: 'edit', label: t('holidayCalendar.edit'), onClick: handleEdit, condition: (row) => canEditCalendar(row, auth) },
      { type: 'copy', label: t('holidayCalendar.copy'), onClick: handleCopy, condition: (row) => !!row?.isGlobal && canCreate },
      { type: 'delete', label: t('holidayCalendar.delete'), onClick: handleDelete, condition: (row) => canEditCalendar(row, auth) },
    ];
  }, [t, isSuperAdmin, canWriteTenant, canCreate, handleRowClick, handleEdit, handleCopy, handleDelete]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const toolbarActions = useMemo(() => (isSuperAdmin ? (
    <Tooltip text={t('holidayCalendar.turkeySeed')}>
      <IconButton
        size="sm"
        aria-label={t('holidayCalendar.turkeySeed')}
        data-testid="turkey-seed"
        onClick={handleTurkeyClick}
        disabled={seeding}
      >
        <CalendarCheck size={16} />
      </IconButton>
    </Tooltip>
  ) : undefined), [isSuperAdmin, t, handleTurkeyClick, seeding]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('holidayCalendar.edit') : t('holidayCalendar.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <GenericFormDrawer
        open={!!copyTarget}
        onClose={() => setCopyTarget(null)}
        title={t('holidayCalendar.copy')}
        schema={copySchema}
        initialValues={copyInitialValues}
        onSave={handleCopySave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('holidayCalendar.deleteTitle')}
        message={t('holidayCalendar.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(EMPTY_DELETE)}
        loading={deleting}
      />

      <ConfirmDialog
        isOpen={!!seedPreview}
        type="primary"
        title={t('holidayCalendar.turkeySeed')}
        message={seedSummary}
        confirmText={t('holidayCalendar.turkeySeed')}
        onConfirm={handleTurkeyConfirm}
        onCancel={() => setSeedPreview(null)}
        loading={seeding}
      />

      {loadFailed && <Note role="alert" data-testid="holiday-calendars-load-failed">{t('holidayCalendar.loadFailed')}</Note>}
      {seedDone && <Note $tone="success" data-testid="turkey-seed-done">{t('holidayCalendar.turkeySeedDone')}</Note>}

      {useMemo(() => (
        <DataTable
          title={t('holidayCalendar.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canCreate}
          newButtonLabel={t('holidayCalendar.new')}
          onNewButtonClick={handleNewButtonClick}
          toolbarActions={toolbarActions}
          onRowsDelete={canCreate ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canCreate, toolbarActions,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
